import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { LoaderService } from '../loader.service';
import { Company } from '../../models/company.model';
import { Manager, ManagerInfoToCreate } from '../../models/manager.model';
import {
  CommonDataProvider,
  ICommonList,
  ICommonListResponse,
  ICommonPaginatedListResponse,
  PaginatedList
} from './common.data-provider';
import { catchError, map } from 'rxjs/operators';
import { User } from '../../models/user.model';
import { Permission } from '../../models/permission.model';
import { PaginationMeta } from '../../models/pagination.model';

@Injectable()
export class CompaniesDataProvider extends CommonDataProvider<Company> {
  private companiesUrl = environment.apiUrl + '/admin/companies';

  constructor(http: HttpClient, loaderService: LoaderService) {
    super(http, loaderService);
  }

  getCompanies(): Observable<ICommonList<Company>> {
    return this.getList(`${this.companiesUrl}?page=1&per_page=500`);
  }

  getCompany(id: number): Observable<Company> {
    return this.getItem(`${this.companiesUrl}/${id}`);
  }

  updateCompany(id: number, body: Partial<Company>): Observable<Company> {
    return this.putAction<Company>(`${this.companiesUrl}/${id}`, body);
  }

  updateManagerPassword(companyId: number, managerId: number, password: string): Observable<void> {
    return this.putAction<void>(
      `${this.companiesUrl}/${companyId}/managers/${managerId}/password`,
      { password }
    );
  }

  createCompany(body: Partial<Company>): Observable<Company> {
    return this.postAction<Company>(`${this.companiesUrl}`, body);
  }

  hideCompany(id: number): Observable<any> {
    return this.http.post(`${this.companiesUrl}/${id}/hide`, {}).pipe(catchError(this.handleError));
  }

  activateCompany(id: number): Observable<any> {
    return this.http
      .post(`${this.companiesUrl}/${id}/activate`, {})
      .pipe(catchError(this.handleError));
  }

  removeCompany(id: number): Observable<any> {
    return this.http.delete(`${this.companiesUrl}/${id}`).pipe(catchError(this.handleError));
  }

  getCompanyManagers(id: number): Observable<ICommonList<Manager>> {
    this.loaderService.show();
    return this.http.get(`${this.companiesUrl}/${id}/managers`).pipe(
      map((data: any) => {
        data.data.items = data.data.managers;
        this.loaderService.hide();
        return data.data;
      }),
      catchError(this.handleError)
    );
  }

  dismissManager(companyId: any, managerId: any): Observable<any> {
    return this.postAction<any>(
      `${this.companiesUrl}/${companyId}/managers/${managerId}/dismiss`,
      null
    );
  }

  dismissInvite(companyId: any, inviteId: any): Observable<any> {
    return this.postAction<any>(
      `${this.companiesUrl}/${companyId}/invites/${inviteId}/dismiss`,
      null
    );
  }

  inviteManager(companyId: any, email: any): Observable<any> {
    return this.postAction<any>(`${this.companiesUrl}/${companyId}/managers/invite`, { email });
  }

  createManager(companyId: number, manager: ManagerInfoToCreate): Observable<Manager> {
    return this.postAction(`${this.companiesUrl}/${companyId}/managers`, manager);
  }

  findManager(companyId: number, email: string, restaurantId?: number): Observable<User> {
    const params: { [key: string]: string } = {
      email
    };
    if (restaurantId) {
      params.restaurantId = restaurantId.toString();
    }

    return this.http.get<any>(`${this.companiesUrl}/${companyId}/manager`, { params }).pipe(
      map((data: any) => data.data.user),
      catchError(this.handleError)
    );
  }

  getCompaniesPaged(
    pagination: PaginationMeta,
    filter = ''
  ): Observable<ICommonPaginatedListResponse<Company> & { permission: Permission }> {
    const filterParam = filter ? `&q=${encodeURIComponent(filter)}` : '';
    return this.getPaginatedList(
      `${this.companiesUrl}?page=${pagination.current_page}&per_page=${pagination.per_page}${filterParam}`
    );
  }

  protected getPaginatedList(
    url: string,
    params?: any
  ): Observable<ICommonPaginatedListResponse<Company> & { permission: Permission }> {
    this.loaderService.show();
    return this.http.get<ICommonListResponse<Company, Array<Company>>>(url, { params }).pipe(
      map((data: ICommonListResponse<Company, Array<Company>>) => {
        const paginatedList = new PaginatedList(data);
        return Object.assign(paginatedList, {
          permission: data.permission as Permission
        });
      }),
      catchError(this.handleError)
    );
  }
}
